import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Star, BadgeCheck, ArrowLeft, ArrowRight } from 'lucide-react';
import Reveal from '@/components/Reveal';
import { FREELANCER_PROFILE, FREELANCER_REVIEWS, UPWORK_PROFILE } from '@/lib/testimonials';
import ProfileEnquiryModal from '@/components/forms/ProfileEnquiryModal';
import { trackEvent, EVENTS } from '@/lib/analytics';

function Stars({ rating }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-steel-light" role="img" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5" fill={i < Math.round(rating) ? 'currentColor' : 'none'} strokeWidth={1.75} />
      ))}
    </span>
  );
}

/* One platform, stated plainly. The action opens an enquiry form rather than
   the external profile, so the label says so instead of promising a link. */
function PlatformRow({ profile, extra, testid, onEnquire }) {
  return (
    <div className="border-t border-white/12 pt-6">
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-display text-lg font-medium tracking-tight text-bone">{profile.platform}</span>
        <span className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-medium text-bone">{profile.rating.toFixed(1)}</span>
          <span className="text-sm text-silver/65">/ 5</span>
        </span>
      </div>
      <p className="mt-1.5 text-sm text-silver/70">{extra}</p>
      {profile.stats && (
        <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
          {profile.stats.map((s) => (
            <div key={s.label}>
              <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-silver/65">{s.label}</dt>
              <dd className="mt-1 font-display text-lg font-medium text-bone">{s.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <button
        type="button"
        onClick={() => onEnquire(profile.platform)}
        data-testid={testid}
        className="group mt-5 inline-flex items-center gap-2 py-2 text-sm font-bold text-silver transition-colors hover:text-bone"
      >
        Request more information
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </div>
  );
}

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [enquirySource, setEnquirySource] = useState(null);
  const reduce = useReducedMotion();
  const total = FREELANCER_REVIEWS.length;
  const review = FREELANCER_REVIEWS[index];
  const go = (dir) => setIndex((i) => (i + dir + total) % total);
  const openEnquiry = (platform) => {
    setEnquirySource(platform);
    trackEvent(EVENTS.profileEnquiryOpen, { source: platform });
  };

  return (
    <>
    <section className="dark-section relative overflow-hidden py-24 sm:py-36" data-testid="testimonials-section">
      <div className="container-wide relative">
        <Reveal className="max-w-3xl">
          <p className="overline-tag text-silver">
            <span className="h-px w-8 bg-steel-light" aria-hidden="true" />
            Real client feedback
          </p>
          {/* Headline and intro no longer promise a click-through, because the
              profile links have been replaced with an enquiry form. Claiming
              verification the visitor cannot perform would be worse than not
              claiming it. */}
          <h2 className="mt-6 font-display text-[2.2rem] sm:text-[2.75rem] lg:text-[3.5rem] tracking-tight leading-[1.04] font-medium text-bone">
            Proof from independent platforms.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-silver">
            HyenaMind is a new company. Rather than claim a client list it does not have yet, this is real
            client feedback for the Google Ads, tracking and lead-generation work behind it, published on
            independent freelance platforms.
          </p>
        </Reveal>

        <div className="mt-16 sm:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-16">
          {/* The review itself is the hero: no card, no border, just typography. */}
          <div className="lg:col-span-7 xl:col-span-8">
            <div className="flex items-center gap-4">
              <span className="font-display text-sm font-semibold tracking-[0.2em] text-silver">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="h-px flex-1 bg-white/12" aria-hidden="true" />
              <span className="font-display text-sm font-semibold tracking-[0.2em] text-silver/60">
                {String(total).padStart(2, '0')}
              </span>
            </div>

            {/* Keyed remount rather than AnimatePresence: with an exit
                animation the outgoing node lingered and could still be on
                screen showing the previous review after the index had already
                moved on. Re-mounting on `index` means what is rendered always
                matches the selected review, and the fade-in still carries the
                change. */}
            <motion.blockquote
                key={index}
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: reduce ? 0 : 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10"
                data-testid="testimonial-featured"
              >
                <p className="font-display font-medium tracking-tight text-bone text-[1.6rem] leading-[1.28] sm:text-[2.1rem] sm:leading-[1.22] xl:text-[2.6rem]">
                  <span aria-hidden="true" className="text-steel-light">“</span>
                  {review.quote}
                  <span aria-hidden="true" className="text-steel-light">”</span>
                </p>

                <footer className="mt-10 flex flex-wrap items-end gap-x-12 gap-y-6">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-silver/65">Client</p>
                    <p className="mt-2 font-display text-xl font-medium tracking-tight text-bone">
                      {review.reviewer}
                    </p>
                  </div>
                  <div className="max-w-xs">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-silver/65">Project</p>
                    <p className="mt-2 text-[15px] leading-snug text-silver">{review.project}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-silver/65">Rating</p>
                    <p className="mt-2.5 flex items-center gap-3">
                      <Stars rating={review.rating} />
                      <span className="text-sm font-bold text-silver/70">on {FREELANCER_PROFILE.platform}</span>
                    </p>
                  </div>
                </footer>

                <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
                  {review.tags.map((t) => (
                    <li key={t} className="text-[11px] font-bold uppercase tracking-[0.12em] text-silver/65">
                      {t}
                    </li>
                  ))}
                </ul>
              </motion.blockquote>

            {/* Manual controls only. Nothing moves while the review is being read. */}
            <div className="mt-12 flex items-center gap-6 border-t border-white/12 pt-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => go(-1)}
                  aria-label="Previous review"
                  data-testid="testimonial-prev"
                  className="flex h-11 w-11 items-center justify-center rounded-md border border-white/15 text-silver transition-colors hover:border-bone hover:text-bone"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => go(1)}
                  aria-label="Next review"
                  data-testid="testimonial-next"
                  className="flex h-11 w-11 items-center justify-center rounded-md border border-white/15 text-silver transition-colors hover:border-bone hover:text-bone"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2" role="tablist" aria-label="Choose a review">
                {FREELANCER_REVIEWS.map((r, i) => (
                  <button
                    key={r.reviewer}
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`Review from ${r.reviewer}`}
                    onClick={() => setIndex(i)}
                    data-testid={`testimonial-dot-${i}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === index ? 'w-10 bg-steel-light' : 'w-4 bg-white/20 hover:bg-white/45'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Verification rail: where the proof lives and how to check it. */}
          <Reveal delay={0.12} className="lg:col-span-5 xl:col-span-4">
            <div className="lg:pl-12 lg:border-l lg:border-white/10">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-silver/65">
                Where this comes from
              </p>
              <p className="mt-4 font-display text-2xl font-medium tracking-tight leading-snug text-bone">
                Independent platforms. Public profiles.
              </p>
              <p className="mt-3 flex items-center gap-2 text-sm text-silver">
                <BadgeCheck className="h-4 w-4 shrink-0 text-steel-light" />
                {FREELANCER_PROFILE.credential}
              </p>

              <div className="mt-10 space-y-8">
                <PlatformRow
                  profile={FREELANCER_PROFILE}
                  extra={`${FREELANCER_PROFILE.reviewCount} reviews`}
                  testid="freelancer-profile-link"
                  onEnquire={openEnquiry}
                />
                <PlatformRow
                  profile={UPWORK_PROFILE}
                  extra={`${UPWORK_PROFILE.reviewCount} reviews · ${UPWORK_PROFILE.jobsCompleted} jobs · ${UPWORK_PROFILE.hoursWorked} hours`}
                  testid="upwork-profile-link"
                  onEnquire={openEnquiry}
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>

    </section>

    <ProfileEnquiryModal
      open={!!enquirySource}
      source={enquirySource || ''}
      onClose={() => setEnquirySource(null)}
    />
    </>
  );
}
